// Seed data for APN development and testing
import {
  collection,
  doc,
  setDoc,
  addDoc,
  deleteDoc
} from 'firebase/firestore';
import { db } from '../firebase';

// Sample groups data
export const sampleGroups = [
  {
    id: '1',
    name: 'Investment Banking Professionals',
    description: 'A community for investment banking analysts, associates, and VPs to share insights, network, and discuss industry trends.',
    category: 'professional',
    memberCount: 45,
    isPrivate: false,
    isActive: true,
    members: ['admin-user-id'], // Will be updated with real user IDs
    moderators: ['admin-user-id'],
    tags: ['investment-banking', 'finance', 'networking']
  },
  {
    id: '2',
    name: 'Private Equity Network',
    description: 'Connect with PE professionals, discuss deal flow, and share market insights.',
    category: 'professional',
    memberCount: 32,
    isPrivate: true,
    isActive: true,
    members: ['admin-user-id'],
    moderators: ['admin-user-id'],
    tags: ['private-equity', 'deals', 'networking']
  },
  {
    id: '3',
    name: 'M&A Specialists',
    description: 'For professionals involved in mergers and acquisitions. Share deal experiences and best practices.',
    category: 'professional',
    memberCount: 28,
    isPrivate: false,
    isActive: true,
    members: ['admin-user-id'],
    moderators: ['admin-user-id'],
    tags: ['mergers', 'acquisitions', 'deals']
  },
  {
    id: '4',
    name: 'New York Finance Professionals',
    description: 'Local networking group for finance professionals in the New York area.',
    category: 'location',
    memberCount: 67,
    isPrivate: false,
    isActive: true,
    members: ['admin-user-id'],
    moderators: ['admin-user-id'],
    tags: ['new-york', 'local', 'networking']
  },
  {
    id: '5',
    name: 'Veterans in Finance',
    description: 'Supporting military veterans transitioning to and advancing in finance careers.',
    category: 'interest',
    memberCount: 23,
    isPrivate: false,
    isActive: true,
    members: ['admin-user-id'],
    moderators: ['admin-user-id'],
    tags: ['veterans', 'military', 'career-transition']
  }
];

// Sample group posts
export const sampleGroupPosts = [
  {
    groupId: '1',
    title: 'Market Outlook for Q1 2025',
    content: 'What are everyone\'s thoughts on the market outlook for the first quarter? Seeing some interesting trends in tech IPOs.',
    authorId: 'admin-user-id',
    authorName: 'The Ghost',
    likes: ['admin-user-id'],
    replies: []
  },
  {
    groupId: '1',
    title: 'Best Practices for Client Presentations',
    content: 'Looking for advice on creating compelling pitch decks. What tools and frameworks do you use?',
    authorId: 'admin-user-id',
    authorName: 'The Ghost',
    likes: [],
    replies: []
  },
  {
    groupId: '2',
    title: 'Deal Sourcing Strategies',
    content: 'How are you finding quality deal flow in the current market? Any new sourcing channels worth exploring?',
    authorId: 'admin-user-id',
    authorName: 'The Ghost',
    likes: [],
    replies: []
  }
];

// Sample jobs
export const sampleJobs = [
  {
    title: 'SAMPLE - Investment Banking Analyst',
    company: 'Goldman Sachs',
    location: 'New York, NY',
    type: 'Full-time',
    experience: 'Entry Level',
    salary: '$150,000 - $180,000',
    description: 'Join our Investment Banking division as an Analyst. You will work on M&A transactions, IPOs, and other corporate finance activities.',
    requirements: [
      'Bachelor\'s degree in Finance, Economics, or related field',
      'Strong analytical and quantitative skills',
      'Proficiency in Excel and PowerPoint',
      'Excellent communication skills'
    ],
    benefits: [
      'Competitive salary and bonus',
      'Health insurance',
      '401(k) matching',
      'Professional development opportunities'
    ],
    postedBy: 'admin-user-id',
    postedByName: 'The Ghost',
    applications: [],
    isActive: true
  },
  {
    title: 'SAMPLE - Private Equity Associate',
    company: 'KKR',
    location: 'San Francisco, CA',
    type: 'Full-time',
    experience: 'Mid Level',
    salary: '$200,000 - $250,000',
    description: 'Seeking an experienced Associate to join our Private Equity team. Focus on deal sourcing, due diligence, and portfolio company management.',
    requirements: [
      'MBA or 3+ years investment banking experience',
      'Strong financial modeling skills',
      'Experience with LBO analysis',
      'Industry expertise preferred'
    ],
    benefits: [
      'Competitive compensation package',
      'Carried interest participation',
      'Comprehensive benefits',
      'Travel opportunities'
    ],
    postedBy: 'admin-user-id',
    postedByName: 'The Ghost',
    applications: [],
    isActive: true
  }
];

// Sample projects
export const sampleProjects = [
  {
    title: 'SAMPLE - Financial Model Review - Tech Startup',
    company: 'Stealth Mode Startup',
    budget: '$5,000 - $10,000',
    duration: '2-3 weeks',
    type: 'Contract',
    description: 'Need an experienced financial modeler to review and enhance our Series A fundraising model. Must have SaaS experience.',
    requirements: [
      'CPA or CFA preferred',
      'SaaS financial modeling experience',
      'Series A fundraising knowledge',
      'Available to start immediately'
    ],
    skills: ['Financial Modeling', 'Excel', 'SaaS', 'Fundraising'],
    postedBy: 'admin-user-id',
    postedByName: 'The Ghost',
    applications: [],
    isActive: true
  },
  {
    title: 'SAMPLE - M&A Due Diligence Support',
    company: 'Mid-Market PE Fund',
    budget: '$15,000 - $25,000',
    duration: '4-6 weeks',
    type: 'Contract',
    description: 'Seeking experienced professionals to support due diligence on a $50M acquisition in the healthcare sector.',
    requirements: [
      'Investment banking or PE experience',
      'Healthcare industry knowledge',
      'Due diligence experience',
      'Available for 40+ hours/week'
    ],
    skills: ['Due Diligence', 'Healthcare', 'Financial Analysis', 'M&A'],
    postedBy: 'admin-user-id',
    postedByName: 'The Ghost',
    applications: [],
    isActive: true
  }
];

// Function to seed all data
export const seedDatabase = async () => {
  try {
    console.log('Starting database seeding...');

    // Clear existing groups first
    console.log('Clearing existing sample groups...');
    for (const group of sampleGroups) {
      try {
        await deleteDoc(doc(db, 'groups', group.id));
        console.log(`Deleted existing group: ${group.id}`);
      } catch (error) {
        // Group doesn't exist, that's fine
        console.log(`Group ${group.id} doesn't exist, skipping delete`);
      }
    }

    // Seed groups with specific IDs
    for (const group of sampleGroups) {
      const groupData = {
        ...group,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      await setDoc(doc(db, 'groups', group.id), groupData);
      console.log(`Created group: ${group.name} with ID: ${group.id}`);
    }

    // Seed group posts
    for (const post of sampleGroupPosts) {
      const postData = {
        ...post,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      await addDoc(collection(db, 'groupPosts'), postData);
      console.log(`Created post: ${post.title}`);
    }

    // Seed jobs
    for (const job of sampleJobs) {
      const jobData = {
        ...job,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      await addDoc(collection(db, 'jobs'), jobData);
      console.log(`Created job: ${job.title}`);
    }

    // Seed projects
    for (const project of sampleProjects) {
      const projectData = {
        ...project,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      await addDoc(collection(db, 'projects'), projectData);
      console.log(`Created project: ${project.title}`);
    }

    console.log('Database seeding completed successfully!');
    return true;
  } catch (error) {
    console.error('Error seeding database:', error);
    console.error('Error details:', error.message);
    throw error;
  }
};

// Function to clear all sample data (for testing)
export const clearSampleData = async () => {
  try {
    console.log('Clearing sample data...');
    // Implementation would go here to delete sample data
    console.log('Sample data cleared!');
  } catch (error) {
    console.error('Error clearing sample data:', error);
    throw error;
  }
};

export default {
  sampleGroups,
  sampleGroupPosts,
  sampleJobs,
  sampleProjects,
  seedDatabase,
  clearSampleData
};
